import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnBuilder } from '@/utils';
import { formatDate } from '@/utils/func';
import { Flex, LogTable, LogTableColumn, RetryAlert } from '../../common';
import { useDormitoryReport, useDormitoryCancelConfirm } from '@/hooks/app';
import { leaveStatusText } from '../helper';

const renderFormat = (date: string) => formatDate(date, 'jYYYY/jMM/jDD');

const PAGE_SIZE = 10;

export default function LeaveReportDesktop({
  items,
  fetch,
  error,
  loading
}: ReturnType<typeof useDormitoryReport>) {

  const [t] = useTranslation(['common', 'dormitory']);
  const [page, setPage] = useState<number>(0);

  const columns = useMemo(() => ColumnBuilder.create([
    { width: 100, key: 'id', title: t('common:labels.id') },
    { key: 'relation', title: t('dormitory:labels.relation') },
    { key: 'address', title: t('dormitory:labels.address') },
    { key: 'mobileNumber', title: t('dormitory:labels.phone-number') },
    { key: 'phoneNumber', title: t('dormitory:labels.landline-phone') },
    { key: 'fromDate', title: t('common:labels.from-date'), render: renderFormat },
    { key: 'toDate', title: t('common:labels:to-date'), render: renderFormat },
    {
      key: 'status',
      title: t('common:labels.status'),
      render: (status: any) => leaveStatusText(status, t)
    },
    { key: ['action'], title: ' ', align: 'center' }
  ]).build() as LogTableColumn[], [t]);

  const onCancelConfirm = useDormitoryCancelConfirm({
    onOk: () => fetch(page, PAGE_SIZE)
  });

  const fetchReport = useCallback(() => {
    fetch(page, PAGE_SIZE);
  }, [page]);

  const rows = items.map(item => ({
    ...item,
    action: (
      <Flex align="center" justify="center">
        {item.status === 'NOT_CHECKED' ? (
          <Tooltip title={t('common:labels.cancel')}>
            <Flex
              inlineFlex
              onClick={() => onCancelConfirm(item)}
              className="p-0.5 h-7 transition-colors rounded-xl cursor-pointer hover:bg-gray-200" align="center" justify="center" >
              <IoCloseCircleOutline className="text-2xl leading-[0] text-red-400 hover:text-red-500" />
            </Flex >
          </Tooltip>
        ) : <Flex className="p-0.5 text-2xl h-7" />}
      </Flex>
    )
  }));

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return (
    error ? (
      <Flex justify="center">
        <RetryAlert noIcon action={fetchReport} content={error} />
      </Flex>
    ) :
      <LogTable
        size="small"
        rows={rows}
        className="no-last-th-border"
        columns={columns}
        loading={loading}
        pagination={{
          // total: total,
          pageSize: PAGE_SIZE,
          current: page + 1,
          onChange: page => setPage(page - 1),
          showSizeChanger: false,
        }}
      />
  );
}
